import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { ArrowBack, ContentCopy, PhoneInTalk } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { DEMO_INTEREST_OPTIONS } from '../config/demoInterestOptions';

type Lead = {
  _id: string;
  contactName?: string;
  businessName?: string;
  zipCode?: string;
  email?: string;
  phone?: string;
  interests?: string[];
  emailOptIn?: boolean;
  smsOptIn?: boolean;
  emailSentAt?: string;
  smsSentAt?: string;
  wantsCall?: boolean;
  source?: 'demo_access' | 'schedule_call';
  notes?: string;
  createdAt: string;
};

type DemoCredentials = {
  email: string;
  password: string;
  loginUrl: string;
  passwordFromEnv?: boolean;
};

type SortKey =
  | 'createdAt'
  | 'contactName'
  | 'businessName'
  | 'zipCode'
  | 'email'
  | 'phone';

const formatPhone = (digits?: string) => {
  if (!digits || digits.length !== 10) return digits || '—';
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const interestLabel = (id: string) =>
  DEMO_INTEREST_OPTIONS.find((o) => o.id === id)?.label || id;

function buildWalkthroughEmail(creds: DemoCredentials | null): string {
  const email = creds?.email || '[demo email — run Ensure/reseed after setting DEMO_PROVIDER_EMAIL]';
  const password = creds?.password || '[demo password — set DEMO_PROVIDER_PASSWORD on the server]';
  const loginUrl = creds?.loginUrl || 'https://www.illinoislawnhub.com/login';

  return `Subject: Your LawnHub provider demo + quick walkthrough

Hi —

Thanks for wanting to try LawnHub. Here’s a sandbox with fake sample customers (Demo Township — not real addresses). One person at a time. About 30 minutes max (or ~10 min idle). When you’re done, log out so the next person can get in.

LOGIN
${loginUrl}
Email: ${email}
Password: ${password}

Yellow banner at the top = demo timer. “Reset demo data” puts the sample world back if you mess it up.

(Prospects also get this auto-emailed / SMS’d when they request a demo.)

FAST TOUR
• Customers: Dashboard → Customer Management
• Schedule: This Week’s Schedule / Scheduling (Edit, Move Day, Add All)
• Crews: Crew Management → Assign Services
• Routes: Route Optimization → Sort and Optimize → Save Routes (per day)
• Jobs: Today’s Jobs → complete / skip
• Extras: Additional Services · Renewals · Tutorials

WHEN YOU’RE READY FOR REAL
https://www.illinoislawnhub.com/get-started

— LawnHub`;
}

const AdminDemoRequestsPage: React.FC = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [seeding, setSeeding] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const [actionMsg, setActionMsg] = useState('');
  const [creds, setCreds] = useState<DemoCredentials | null>(null);
  const [copied, setCopied] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const walkthrough = useMemo(() => buildWalkthroughEmail(creds), [creds]);

  const sortedLeads = useMemo(() => {
    const copy = [...leads];
    copy.sort((a, b) => {
      // Keep "wants a call" rows at the top so they're easy to spot
      if (!!a.wantsCall !== !!b.wantsCall) {
        return a.wantsCall ? -1 : 1;
      }
      const av = String((a as any)[sortKey] ?? '').toLowerCase();
      const bv = String((b as any)[sortKey] ?? '').toLowerCase();
      if (sortKey === 'createdAt') {
        const at = new Date(a.createdAt).getTime();
        const bt = new Date(b.createdAt).getTime();
        return sortDir === 'asc' ? at - bt : bt - at;
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return copy;
  }, [leads, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'createdAt' ? 'desc' : 'asc');
    }
  };

  const loadCreds = async () => {
    try {
      const res = await apiService.get('/demo/admin/credentials');
      if (res.success && res.data) {
        setCreds(res.data as DemoCredentials);
      }
    } catch (err: any) {
      console.warn('[DEMO] Could not load credentials:', err?.message);
    }
  };

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await apiService.get('/leads/demo-requests');
      if (res.success) {
        setLeads((res.data as Lead[]) || []);
      } else {
        setError((res as any).error || 'Failed to load demo requests');
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to load demo requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadCreds();
  }, []);

  const ensureSeed = async () => {
    setSeeding(true);
    setActionMsg('');
    try {
      const res = await apiService.post('/demo/admin/ensure-seed', {});
      setActionMsg((res as any).message || 'Demo seeded.');
      await loadCreds();
    } catch (err: any) {
      setActionMsg(err?.message || 'Seed failed');
    } finally {
      setSeeding(false);
    }
  };

  const forceRelease = async () => {
    setReleasing(true);
    setActionMsg('');
    try {
      const res = await apiService.post('/demo/admin/force-release', {});
      setActionMsg((res as any).message || 'Demo released.');
    } catch (err: any) {
      setActionMsg(err?.message || 'Release failed');
    } finally {
      setReleasing(false);
    }
  };

  const copyWalkthrough = async () => {
    try {
      await navigator.clipboard.writeText(walkthrough);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setActionMsg('Could not copy — select the text manually.');
    }
  };

  const SortHeader = ({ id, label }: { id: SortKey; label: string }) => (
    <TableCell sortDirection={sortKey === id ? sortDir : false}>
      <TableSortLabel
        active={sortKey === id}
        direction={sortKey === id ? sortDir : 'asc'}
        onClick={() => toggleSort(id)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/admin')} sx={{ mb: 2 }}>
        Back to Admin
      </Button>
      <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
        Demo Login Requests
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Prospects who requested the interactive demo (or asked for a call after trying it). Login is
        auto-emailed/texted when they opt in. Orange-highlighted rows want a call / virtual meet.
        Click column headers to sort. Shared sandbox: one active session (30 min hard / 10 min idle).
      </Typography>

      <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Backup outreach email (usually auto-sent)
          </Typography>
          <Button size="small" variant="contained" startIcon={<ContentCopy />} onClick={copyWalkthrough}>
            {copied ? 'Copied' : 'Copy email'}
          </Button>
        </Box>
        {creds && !creds.passwordFromEnv && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            DEMO_PROVIDER_PASSWORD is not set on the server — you&apos;re seeing the built-in default.
          </Alert>
        )}
        {creds && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Login filled from server: {creds.email}
          </Typography>
        )}
        <Typography
          component="pre"
          sx={{
            whiteSpace: 'pre-wrap',
            fontFamily: 'inherit',
            fontSize: 13,
            m: 0,
            maxHeight: 280,
            overflow: 'auto',
          }}
        >
          {walkthrough}
        </Typography>
      </Paper>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
        <Button variant="outlined" onClick={ensureSeed} disabled={seeding}>
          {seeding ? 'Seeding…' : 'Ensure / reseed demo account'}
        </Button>
        <Button variant="outlined" color="warning" onClick={forceRelease} disabled={releasing}>
          {releasing ? 'Releasing…' : 'Force release demo lock'}
        </Button>
      </Box>
      {actionMsg && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {actionMsg}
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && leads.length === 0 && (
        <Alert severity="info">No demo requests yet.</Alert>
      )}

      {!loading && leads.length > 0 && (
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <SortHeader id="createdAt" label="When" />
                <TableCell>Type</TableCell>
                <SortHeader id="contactName" label="Name" />
                <SortHeader id="businessName" label="Business" />
                <SortHeader id="zipCode" label="ZIP" />
                <SortHeader id="email" label="Email" />
                <SortHeader id="phone" label="Phone" />
                <TableCell>Looking for</TableCell>
                <TableCell>Delivery</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedLeads.map((lead) => (
                <TableRow
                  key={lead._id}
                  sx={
                    lead.wantsCall
                      ? {
                          bgcolor: 'rgba(255, 152, 0, 0.18)',
                          '&:hover': { bgcolor: 'rgba(255, 152, 0, 0.28)' },
                          borderLeft: '4px solid',
                          borderLeftColor: 'warning.dark',
                        }
                      : undefined
                  }
                >
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {new Date(lead.createdAt).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    {lead.wantsCall ? (
                      <Chip
                        size="small"
                        color="warning"
                        icon={<PhoneInTalk />}
                        label="Wants a call"
                        sx={{ fontWeight: 700 }}
                      />
                    ) : (
                      <Chip size="small" variant="outlined" label="Demo access" />
                    )}
                  </TableCell>
                  <TableCell>{lead.contactName || '—'}</TableCell>
                  <TableCell>{lead.businessName || '—'}</TableCell>
                  <TableCell>{lead.zipCode || '—'}</TableCell>
                  <TableCell>{lead.email || '—'}</TableCell>
                  <TableCell>{formatPhone(lead.phone)}</TableCell>
                  <TableCell sx={{ minWidth: 180 }}>
                    {lead.wantsCall
                      ? lead.notes || 'Call / virtual meet after demo'
                      : lead.interests?.length
                        ? lead.interests.map(interestLabel).join('; ')
                        : lead.notes || '—'}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 12 }}>
                    {lead.wantsCall ? (
                      'Follow up to schedule'
                    ) : (
                      <>
                        Email: {lead.emailOptIn ? (lead.emailSentAt ? 'sent' : 'opted-in') : '—'}
                        <br />
                        SMS: {lead.smsOptIn ? (lead.smsSentAt ? 'sent' : 'opted-in') : '—'}
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
};

export default AdminDemoRequestsPage;
